import { SelectDropdown } from 'components/dropdowns/select/select';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable } from 'knockout';
import AdminCommunityPagesManager from 'managers/community/pages/admin';
import SimpleTopicsManager from 'managers/discussion/simpleTopics';
import { IWCCStorageManager } from 'managers/iStorage';
import { EffectsContainer } from 'mixins/withEffects';
import CommunityPage from 'models/community/page';
import CommunityNavigationWidgetMode from '../enums/modes';
import { CommunityNavigationWidgetLinkModel } from '../link';
import { CommunityNavigationWidgetModel } from '../navigation';
import './layout.html';
import './layout.scss';

components
    .registerLocal('community-navigation-layout-link', () => require('./link'))
    .preload('wcc-select');

const { discussionId } = settings;

const systemPagesWhitelist = [
    enums.CommunityPageType.LogIn.value,
    enums.CommunityPageType.SignUp.value
]

const pagesBlackList = [
    enums.CommunityPageType.TermsAndConditionsChangeSummary.value
]

export interface CommunityNavigationWidgetLayoutVMConfig {
    navigation: CommunityNavigationWidgetModel

    onSave?: Action
    onCancel?: Action
}

@injectable()
export default class CommunityNavigationWidgetLayoutVM {
    private navigation: CommunityNavigationWidgetModel

    private mode: Subscribable<CommunityNavigationWidgetMode>
    private links: Subscribable<Array<CommunityNavigationWidgetLinkModel>>
    private regularPages: Subscribable<Array<CommunityPage>>
    private isLogOutLinkShown: Subscribable<boolean>

    showErrors = ko.observable(false);

    publicLinks = ko.observableArray<CommunityNavigationWidgetLinkModel>().extend({ deferred: true });
    protectedLinks = ko.observableArray<CommunityNavigationWidgetLinkModel>().extend({ deferred: true });

    modeSelect: SelectDropdown<CommunityNavigationWidgetMode>
    logoutSelect: SelectDropdown<boolean>

    ready: Subscribable<boolean>
    loading: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) private config: CommunityNavigationWidgetLayoutVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.navigation = config.navigation;
        this.links = this.publicLinks.concat(this.protectedLinks);

        const pagesManager = storage.get(AdminCommunityPagesManager, {});
        const pagesReady = pagesManager.pluck(m => !m.loading(), false).extend({ deferred: true });
        const pages = pagesManager.pluck(m => m.pages, []);
        this.regularPages = pages.filter(page => this.checkIfPageAllowed(page));

        const topicsManager = storage.get(SimpleTopicsManager, { discussionId });
        const topicsReady = topicsManager.pluck(m => !m.loading(), false).extend({ deferred: true });

        this.modeSelect = new SelectDropdown({
            items: [
                { title: 'Auto', value: CommunityNavigationWidgetMode.auto },
                { title: 'Desktop', value: CommunityNavigationWidgetMode.desktop },
                { title: 'Mobile', value: CommunityNavigationWidgetMode.mobile }
            ]
        });

        this.mode = this.modeSelect.selectedItem.pluck(item => item.value, CommunityNavigationWidgetMode.auto);

        this.logoutSelect = new SelectDropdown({
            items: [
                { title: 'Show', value: true },
                { title: 'Hide', value: false }
            ]
        });

        this.isLogOutLinkShown = this.logoutSelect.selectedItem.pluck(item => item.value, false);

        this.ready = ko.pureComputed(() => pagesReady() && topicsReady()).extend({ rateLimit: 0 });
        this.loading = ko.pureComputed(() => !this.ready());

        this.modeSelect.select(item => item.value == this.navigation.mode());
        this.logoutSelect.select(item => item.value == this.navigation.showLogOutLink());

        effects.register(ready => {
            if (ready)
                this.loadLinks();
        }, [this.ready]);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'community__navigation__layout': true
            }
        });
    }

    save = () => {
        this.showErrors(false).showErrors(true);

        if (!this.validate()) return;

        this.navigation.mode(this.mode());
        this.navigation.showLogOutLink(this.isLogOutLinkShown());

        this.navigation.links(this.links());
        this.navigation.mobileLinks([]);

        this.config.onSave?.();
    }

    cancel = () => {
        this.config.onCancel?.();
    }

    resetToDefault = () => {
        var links = this.regularPages()
            .filter(page => !pagesBlackList.includes(<number>page.type()))
            .map(page => {
                return new CommunityNavigationWidgetLinkModel({
                    title: page.name(),
                    route: page.url(),
                    isProtected: page.access() == enums.CommunityPageAccess.LoggedInMembersOnly.value
                });
            });

        const publicLinks = links.filter(link => !link.isProtected());
        const protectedLinks = links.filter(link => link.isProtected());

        this.publicLinks(publicLinks);
        this.protectedLinks(protectedLinks);

        this.modeSelect.select(item => item.value == CommunityNavigationWidgetMode.auto);
        this.logoutSelect.select(item => item.value == true);
    }

    addPublicLink = () => {
        this.addLink();
    }

    addProtectedLink = () => {
        this.addLink(true);
    }

    removeLink = (link: CommunityNavigationWidgetLinkModel) => {
        this.publicLinks.remove(link);
        this.protectedLinks.remove(link);
    }

    moveLink = (link: CommunityNavigationWidgetLinkModel, offset: number) => {
        const collection = this.publicLinks().indexOf(link) != -1 ? this.publicLinks : this.protectedLinks;

        let oldIdx = _(collection()).indexOf(link),
            newIdx = oldIdx + offset;

        if (oldIdx == -1) return;
        if (newIdx < 0) newIdx = 0;
        if (newIdx >= collection().length) newIdx = collection().length - 1;

        collection.splice(newIdx, 0, collection.splice(oldIdx, 1)[0]);
    }

    private checkIfPageAllowed(page: CommunityPage) {
        return page.type() !== enums.CommunityPageType.Master.value &&
            (!page.isSystem() || _(systemPagesWhitelist).contains(page.type()));
    }

    private addLink(isProtected = false) {
        const collection = isProtected ? this.protectedLinks : this.publicLinks;

        collection.push(new CommunityNavigationWidgetLinkModel({ isProtected: isProtected }));
    }

    private loadLinks() {
        const links = this.navigation.links().map(link => link.clone<CommunityNavigationWidgetLinkModel>());
        const publicLinks = links.filter(link => !link.isProtected());
        const protectedLinks = links.filter(link => link.isProtected());

        this.publicLinks(publicLinks);
        this.protectedLinks(protectedLinks);
    }

    private validate() {
        return this.links().every(link => link.validate());
    }
}